/* NVN Marine — product card adapters for ES search results
 *
 * Builds HTML strings matching snippets/product-card.liquid and
 * snippets/product-card-list.liquid so existing CSS, hover quick-add,
 * and data-item-* analytics attributes Just Work.
 *
 * Exposes:
 *   window.NVN.cards.renderGridCard(esProduct) -> HTML string
 *   window.NVN.cards.renderListCard(esProduct) -> HTML string
 *   window.NVN.cards.renderPredictiveItem(esProduct) -> HTML string (autocomplete row)
 */
(function () {
  'use strict';

  var NVN = (window.NVN = window.NVN || {});

  // ES strings arrive HTML-encoded (e.g. `Garmin 45&quot;`). Decode first,
  // then re-escape for safe DOM insertion.
  function dec(s) { return (NVN.decode || function (v) { return v == null ? '' : String(v); })(s); }
  function esc(s) {
    return dec(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  // Allow <em> tags from ES highlighting; escape everything else.
  function escAllowEm(s) {
    if (s == null) return '';
    var safe = esc(s);
    return safe.replace(/&lt;em&gt;/g, '<em>').replace(/&lt;\/em&gt;/g, '</em>');
  }
  function stripSku(sku) { return String(sku || '').replace(/^CWR-/, ''); }
  function money(n) { return (NVN.money || function (v) { return '$' + Number(v || 0).toFixed(2); })(n); }

  function pickVariant(p) {
    return (p.variants && p.variants[0]) || {};
  }

  // MPN / UPC may show up in several places depending on how the ES service
  // indexes them. Probe all plausible spots so cards light up automatically
  // when the upstream index adds the fields.
  function pickMpn(p, v) {
    return v.mpn || v.manufacturer_part_number ||
      p.mpn ||
      (p.metafields && p.metafields.meta && p.metafields.meta.mpn) ||
      (p.metafields && p.metafields.specs && p.metafields.specs.mpn) ||
      '';
  }
  function pickUpc(p, v) {
    return v.barcode || v.upc ||
      p.upc || p.barcode ||
      (p.metafields && p.metafields.meta && p.metafields.meta.upc) ||
      '';
  }

  function priceBlock(p) {
    var min = (p.price && p.price.min) || 0;
    var compareMin = (p.price && p.price.compare_at_min) || 0;
    return { now: min, was: compareMin > min ? compareMin : 0 };
  }

  function imageTag(src, alt, sizes) {
    if (!src) return '<div class="prod-ph dark"></div>';
    return '<img src="' + esc(src) + '" alt="' + esc(alt || '') + '" loading="lazy"' +
      (sizes ? ' sizes="' + esc(sizes) + '"' : '') + '>';
  }

  // Shipping profile badge — mirrors snippets/shipping-badge.liquid.
  // Same priority and compact mode contract so the storefront card and the
  // ES-rendered card always show the same shipping signal for a given
  // product. ES exposes the tags array on each hit.
  var TRUCK_SVG  = '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
  var BOLT_SVG   = '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>';
  var SHIELD_SVG = '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>';

  function shippingBadge(p, compact) {
    var tags = p && p.tags;
    if (!tags || !tags.length) return '';
    function has(t) { return tags.indexOf(t) !== -1; }
    if (has('X_PROFILE_FAST_AND_FREE'))      return '<span class="badge badge-fast-free">' + BOLT_SVG + ' Fast &amp; Free</span>';
    if (has('X_PROFILE_FREE_SHIPPING'))      return '<span class="badge badge-free-ship">' + TRUCK_SVG + ' Free Shipping</span>';
    if (has('X_PROFILE_TRUCK_FREIGHT_FREE')) return '<span class="badge badge-truck-free">' + TRUCK_SVG + ' Truck Freight &middot; Free Shipping</span>';
    if (has('X_PROFILE_TRUCK_FREIGHT'))      return compact ? '' : '<span class="badge badge-freight">' + TRUCK_SVG + ' Truck Freight</span>';
    if (has('X_PROFILE_GROUND_HAZMAT'))      return compact ? '' : '<span class="badge badge-hazmat">' + SHIELD_SVG + ' Ground (Hazmat)</span>';
    if (has('X_PROFILE_GENERAL'))            return '';
    // Legacy fallback for partially-migrated catalogs
    if (has('X_TRANSPORT_TRUCK'))            return compact ? '' : '<span class="badge badge-freight">' + TRUCK_SVG + ' Truck Freight ($299)</span>';
    if (has('X_CAMP_SHIPFREE'))              return '<span class="badge badge-free-ship">' + TRUCK_SVG + ' Free shipping</span>';
    return '';
  }

  function stockBadge(p, v) {
    // p.in_stock is the product-level rollup from ES — authoritative for grid cards.
    // v.available can lag (stale variant index), so we don't short-circuit on it.
    if (p.in_stock === false) {
      return '<span class="stock out"><span class="d"></span>Out of stock</span>';
    }
    var qty = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity
            : (typeof p.total_inventory === 'number' ? p.total_inventory : null);
    if (qty !== null && qty > 0 && qty <= 3) {
      return '<span class="stock low"><span class="d"></span>Low stock</span>';
    }
    return '<span class="stock"><span class="d"></span>In stock</span>';
  }

  function dataAttrs(p, v, index) {
    var price = ((p.price && p.price.min) || 0).toFixed(2);
    var variant = v.title && v.title !== 'Default Title' ? v.title : '';
    var url = p.url || (p.handle ? '/products/' + p.handle : '');
    var img = p.featured_image || '';
    return ' data-product-card' +
      ' data-item-id="' + esc('shopify_us_' + p.id + '_' + v.id) + '"' +
      ' data-item-name="' + esc(p.title || '') + '"' +
      ' data-item-brand="' + esc(p.vendor || '') + '"' +
      ' data-item-category="' + esc(p.product_type || '') + '"' +
      ' data-item-variant="' + esc(variant) + '"' +
      ' data-item-price="' + esc(price) + '"' +
      ' data-item-sku="' + esc(stripSku(v.sku)) + '"' +
      ' data-item-product-url="' + esc(url) + '"' +
      ' data-item-image-url="' + esc(img) + '"' +
      (typeof index === 'number' ? ' data-index="' + index + '"' : '');
  }

  function quickAddBtn(p, v) {
    if (p.in_stock === false) return '';
    var title = esc(p.title || '');
    return '<button type="button" class="quick-add-btn" data-quick-add' +
      ' data-variant-id="' + esc(v.id || '') + '"' +
      ' data-product-title="' + title + '"' +
      ' aria-label="Add ' + title + ' to cart">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.94 1.61h9.72a2 2 0 001.94-1.61L23 6H6"/></svg>' +
      ' Add to cart' +
      '</button>';
  }

  /* -- Grid card (matches snippets/product-card.liquid) ------------------- */
  function renderGridCard(p, index) {
    var v = pickVariant(p);
    var pr = priceBlock(p);
    var onSale = pr.was > pr.now;
    var url = p.url || ('/products/' + (p.handle || ''));
    var sku = stripSku(v.sku);

    var html = '<div class="product-card"' + dataAttrs(p, v, index) + '>';
    html += '<a class="card-link" href="' + esc(url) + '" aria-label="' + esc(p.title || '') + '">';
    html += '<div class="img">' + imageTag(p.featured_image, p.title, '(min-width:990px) 25vw, 50vw') + '</div>';
    html += '<div class="body">';
    html += '<div class="brand">' + esc(p.vendor || '') + '</div>';
    html += '<div class="name">' + esc(p.title || '') + '</div>';
    var mpn = pickMpn(p, v);
    var upc = pickUpc(p, v);
    if (sku || mpn || upc) {
      var parts = [];
      if (sku) parts.push('<b>SKU: </b>' + esc(sku));
      if (upc) parts.push('<b>UPC: </b>' + esc(upc));
      if (mpn) parts.push('<b>MPN: </b>' + esc(mpn));
      html += '<div class="sku">' + parts.join(' · ') + '</div>';
    }
    var shipHtml = shippingBadge(p, true);
    if (shipHtml) html += '<div class="card-ship">' + shipHtml + '</div>';
    html += '<div class="foot">';
    html += '<div class="price-row"><span class="price">' + money(pr.now) + '</span>';
    if (onSale) {
      var save = pr.was - pr.now;
      html += '<div class="price-stack">';
      html += '<span class="was">' + money(pr.was) + '</span>';
      html += '<span class="save">Save ' + money(save) + '</span>';
      html += '</div>';
    }
    html += '</div>';
    html += stockBadge(p, v);
    html += '</div></div></a>';
    html += quickAddBtn(p, v);
    html += '</div>';
    return html;
  }

  /* -- List card (matches snippets/product-card-list.liquid) -------------- */
  function renderListCard(p, index) {
    var v = pickVariant(p);
    var pr = priceBlock(p);
    var onSale = pr.was > pr.now;
    var url = p.url || ('/products/' + (p.handle || ''));
    var sku = stripSku(v.sku);
    var desc = p.description ? String(p.description).replace(/<[^>]*>/g, '').slice(0, 280) : '';
    if (desc.length === 280) desc = desc.replace(/\s+\S*$/, '') + '…';

    var stockState = 'in';
    if (p.in_stock === false) stockState = 'out';
    else {
      var qty = (typeof v.inventory_quantity === 'number') ? v.inventory_quantity
              : (typeof p.total_inventory === 'number' ? p.total_inventory : null);
      if (qty !== null && qty > 0 && qty <= 3) stockState = 'low';
    }

    var html = '<article class="pl-card"' + dataAttrs(p, v, index) + '>';
    html += '<a class="pl-img" href="' + esc(url) + '" aria-hidden="true" tabindex="-1">';
    html += imageTag(p.featured_image, p.title, '(min-width:990px) 200px, 30vw');
    if (onSale) html += '<span class="pl-flag pl-sale">Sale</span>';
    html += '</a>';

    html += '<div class="pl-body">';
    html += '<div class="pl-brand">' + esc(p.vendor || '') + '</div>';
    html += '<h3 class="pl-title"><a href="' + esc(url) + '">' + esc(p.title || '') + '</a></h3>';
    var lMpn = pickMpn(p, v);
    var lUpc = pickUpc(p, v);
    if (sku || lMpn || lUpc) {
      var lParts = [];
      if (sku) lParts.push('SKU ' + esc(sku));
      if (lUpc) lParts.push('<b>UPC: </b>' + esc(lUpc));
      if (lMpn) lParts.push('<b>MPN: </b>' + esc(lMpn));
      html += '<div class="pl-sku">' + lParts.join(' · ') + '</div>';
    }
    if (desc) html += '<p class="pl-desc">' + esc(desc) + '</p>';
    var lShipHtml = shippingBadge(p, false);
    if (lShipHtml) html += '<div class="pl-meta">' + lShipHtml + '</div>';
    html += '</div>';

    html += '<div class="pl-buy">';
    html += '<div class="pl-price">';
    if (onSale) html += '<span class="was">' + money(pr.was) + '</span>';
    html += '<span class="now">' + money(pr.now) + '</span>';
    html += '</div>';
    html += '<div class="pl-stock">';
    if (stockState === 'in') html += '<span class="badge badge-stock"><span class="dot"></span>In stock</span>';
    else if (stockState === 'low') html += '<span class="badge badge-low"><span class="dot"></span>Low stock</span>';
    else html += '<span class="badge badge-out"><span class="dot"></span>Out of stock</span>';
    html += '</div>';
    html += '<a href="' + esc(url) + '" class="btn btn-primary btn-sm">View product</a>';
    html += '</div>';
    html += '</article>';
    return html;
  }

  /* -- Predictive search row (autocomplete dropdown) ---------------------- */
  function renderPredictiveItem(p) {
    var v = pickVariant(p);
    var pr = priceBlock(p);
    var url = p.url || ('/products/' + (p.handle || ''));
    var sku = stripSku(v.sku);
    var titleHTML = p.highlighted ? escAllowEm(p.highlighted) : esc(p.title || '');
    var img = p.image || p.featured_image;

    var html = '<a href="' + esc(url) + '" class="ps-product" data-ps-item>';
    if (img) {
      html += '<img class="ps-product-img" src="' + esc(img) + '" alt="' + esc(p.title || '') +
        '" width="44" height="44" loading="lazy">';
    } else {
      html += '<div class="ps-product-img-placeholder"></div>';
    }
    html += '<div class="ps-product-info">';
    if (p.vendor) html += '<div class="ps-product-vendor">' + esc(p.vendor) + '</div>';
    html += '<div class="ps-product-title">' + titleHTML + '</div>';
    html += '<div class="ps-product-price">';
    html += money(pr.now);
    if (pr.was > pr.now) html += '<span class="ps-product-compare">' + money(pr.was) + '</span>';
    if (sku) html += '<span class="ps-product-sku">SKU: ' + esc(sku) + '</span>';
    html += '</div>';
    html += '</div>';
    if (p.in_stock === false) html += '<span class="ps-product-badge">Sold out</span>';
    html += '</a>';
    return html;
  }

  NVN.cards = {
    renderGridCard: renderGridCard,
    renderListCard: renderListCard,
    renderPredictiveItem: renderPredictiveItem
  };
})();
